import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  ChevronDown,
  CircleHelp,
  Globe,
  LogOut,
  Settings,
  UserRound,
} from "lucide-react";
import { ROLE_LABELS } from "@pakker/shared";
import { useAuth } from "../features/auth/auth-context.js";
import { Button } from "../ui/button.js";
import { BrandLogo } from "../ui/brand-logo.js";
import { AccountKeyControls } from "../features/agent-planning/account-key-controls.js";

export function AppShell() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const menu = useRef<HTMLDetailsElement>(null);
  const trigger = useRef<HTMLElement>(null);
  useEffect(() => {
    if (menu.current) menu.current.open = false;
  }, [location.key]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (
        menu.current?.open &&
        event.target instanceof Node &&
        !menu.current.contains(event.target)
      )
        menu.current.open = false;
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && menu.current?.open) {
        menu.current.open = false;
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, []);
  const signOut = async () => {
    setSigningOut(true);
    setLogoutError(null);
    try {
      await auth.logout();
      navigate("/login?status=signed-out", { replace: true });
    } catch {
      setLogoutError("Could not sign you out. Try again.");
    } finally {
      setSigningOut(false);
    }
  };
  return (
    <div className="product-shell shipment-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <div className="product-workspace">
        <header className="product-topbar">
          <Link aria-label="PakPlan home" className="shipment-brand" to="/">
            <BrandLogo />
          </Link>
          <div className="topbar-actions">
            <details
              className="user-menu"
              ref={menu}
              onKeyDown={(event) => {
                if (event.key === "Escape" && event.currentTarget.open) {
                  event.preventDefault();
                  event.currentTarget.open = false;
                  trigger.current?.focus();
                }
              }}
            >
              <summary aria-label={`Profile for ${auth.user?.name ?? "you"}`} ref={trigger}>
                <UserRound aria-hidden="true" size={20} />
                <span className="user-menu-name" title={auth.user?.name}>
                  {auth.user?.name ?? "Profile"}
                </span>
                <ChevronDown className="user-menu-chevron" aria-hidden="true" size={16} />
              </summary>
              <div className="user-menu-panel">
                <div className="user-menu-identity">
                  <p>
                    <strong>{auth.user?.name}</strong>
                    <span>{auth.user?.email}</span>
                  </p>
                  {auth.organization ? (
                    <p className="user-menu-organization">
                      <span>{auth.organization.name}</span>
                      <span className="user-menu-role">{ROLE_LABELS[auth.organization.role]}</span>
                    </p>
                  ) : null}
                </div>
                {auth.status === "authenticated" &&
                auth.organization &&
                (auth.organization.role === "admin" || auth.organization.role === "planner") ? (
                  <AccountKeyControls
                    key={`${auth.user?.id}:${auth.organization.id}`}
                    orgId={auth.organization.id}
                  />
                ) : null}
                <Link to="/how-to/ai-agents">
                  <BookOpen aria-hidden="true" size={18} />
                  Plan with an AI agent
                </Link>
                <Link
                  onClick={() => {
                    if (menu.current) menu.current.open = false;
                  }}
                  to="/settings"
                >
                  <Settings aria-hidden="true" size={18} />
                  Settings
                </Link>
                <Link
                  onClick={() => {
                    if (menu.current) menu.current.open = false;
                  }}
                  to="/help"
                >
                  <CircleHelp aria-hidden="true" size={18} />
                  Help
                </Link>
                <Link to="/welcome">
                  <Globe aria-hidden="true" size={18} />
                  Visit website
                </Link>
                <div className="user-menu-session">
                  <Button disabled={signingOut} onClick={() => void signOut()} variant="ghost">
                    <LogOut aria-hidden="true" size={18} />
                    {signingOut ? "Signing out…" : "Sign out"}
                  </Button>
                </div>
              </div>
            </details>
          </div>
        </header>
        {logoutError === null ? null : (
          <div className="shell-alert" role="alert">
            {logoutError}
          </div>
        )}
        <main className="product-main" id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
